import { Inject } from '@odinjs/odin';

import { catchRenderException, shouldRenderMobile, TokenUtils } from '@philips-emr/hpms-components';
import { AvatarAPI, FetchAPI, LinkAPI, NotificationCenterAPI } from '@philips-emr/tws-components';
import { ThemeTonalRange, Type } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnClick, OnMount } from '@philips-emr/tws-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { AuthenticationService, HttpService, ModalService, ThemeService } from '@philips-emr/tws-core/lib/services';
import { applicationContext, foundationContext } from '@philips-emr/tws-foundation';

import { setupDinamico } from '../../app/module-registry';
import { ApplicationAliases as Aliases, FragmentNames, RootRoutes } from '../../codegen';
import { Endpoints } from '../../definitions/endpoints';
import { Routes } from '../../definitions/routes';
import { AditivoContratual } from '../../interfaces/aditivio-contratual';
import { pollingService } from '../../services';
import ContractAmendmentService from '../../services/contract-amendment-service';
import TermsService from '../../services/terms-service';
import { Mensagem } from '../mensagens/mensagens-controller';

import BaseController from './base-controller';

enum MENU_NOTIFICACAO {
  NOTIFICATION = 'NotificationClick',
  MARCAR_COMO_LIDA = 'MarkAsRead',
  MARK_ALL_AS_READ = 'MarkAllAsRead',
}

enum STATUS_NOTIFICACAO {
  SIM = 'S',
  NAO = 'N'
}

interface Notificacao {
  nrSequencia: number,
  dsTitulo: string,
  dsTexto: string,
  ieLeitura: STATUS_NOTIFICACAO,
  dtNotificacao: string,
  qtdadeNaoLidas: number
}

const TEMPO_POOLING_MENSAGEM = 100000;
@Controller({ fragment: FragmentNames.APPLICATION })
class ApplicationController extends BaseController {
  @Inject(Aliases.LINK_MENSAGEM)
  private linkMessage: LinkAPI;
  @Inject(Aliases.NOTIFICATION_CENTER)
  private notificationCenter: NotificationCenterAPI;

  @OnClick('accessibility')
  onAccessibilityClick(): void {
    ModalService.openDefault({
      title: 'beneficiary.acessibilidade',
      closeable: true,
      fragment: 'modal-acessibilidade',
    });
  }

  @OnMount(Aliases.LAYOUT_APPLICATION)
  onMountLayoutApplication(): void {
    const { nrSeqBeneficiario, cdPessoaFisica } = TokenUtils.getValues();
    this.context.set({ nrSeqBeneficiario, cdPessoaFisica });
    applicationContext.openModule('inicio');
    setupDinamico();
    ThemeService.setContentTonalRange(ThemeTonalRange.ULTRA_LIGHT);
    TermsService.checkTerms();
    ContractAmendmentService.checkAmends(nrSeqBeneficiario);
  }

  @OnMount(Aliases.LINK_MENSAGEM)
  onMountFragmentMessages(): void {
    pollingService.createPolling('mensagem', this.mensagensNaoLidas.bind(this), TEMPO_POOLING_MENSAGEM);
    this.mensagensNaoLidas();
    this.context.set({ mensagensNaoLidas: this.mensagensNaoLidas.bind(this) });
  }

  public mensagensNaoLidas(): void {
    const { nrSeqBeneficiario } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoints.GET.QTDADE_MENSAGENS_NAO_LIDAS, { nrSeqBeneficiario })
      .then(response => {
        const { body } = response;
        const { qtNaoLidas } = body as Mensagem;
        this.linkMessage.setBadge(!qtNaoLidas ? '' : qtNaoLidas);
      }).catch(() => catchRenderException(() => this.linkMessage.setBadge('')));
  }

  @OnClick(Aliases.OPTION_MEUS_DADOS)
  onClickOptionMeusDados(): void {
    this.historyService.push(Routes.MEUS_DADOS);
  }

  @OnClick(Aliases.OPTION_EXIT)
  onClickOptionExit(): void {
    pollingService.removePolling('mensagem');
    void AuthenticationService.logout();
    applicationContext.redirect(RootRoutes.ROUTE_LOGIN);
  }

  @OnClick([Aliases.LINK_MOSTRAR_TODAS_MENSAGENS, Aliases.OPTION_MENSAGENS])
  onClickLinkShowAllMessages = (): void => this.historyService.push(Routes.MENSAGENS);

  @OnAfterFetch(Aliases.FETCH_NOTIFICACOES)
  onAfterFetchNotifications(event: FetchEvent): void {
    const api = event.target as FetchAPI;
    if (event.data) {
      const notificacoes = event.data as Notificacao[];
      const notificacaoFormatada = notificacoes.map((data, index) => {
        const novaPagina = index >= (notificacoes.length - 10);
        if (!novaPagina) return data;
        const notificacaoLida = data.ieLeitura === STATUS_NOTIFICACAO.SIM;
        return {
          date: data.dtNotificacao,
          description: data.dsTexto,
          id: data.nrSequencia,
          read: notificacaoLida,
          title: data.dsTitulo,
          type: Type.NOTICE,
        };
      });
      api.setState({ data: notificacaoFormatada });
    }
  }

  @OnMount(Aliases.NOTIFICATION_CENTER)
  onMountNotificationCenter(): void {
    pollingService.createPolling('notificacao', this.notificacoesNaoLidas.bind(this), TEMPO_POOLING_MENSAGEM);
    this.notificacoesNaoLidas();
  }

  @OnClick(Aliases.NOTIFICATION_CENTER)
  onClickNotificationCenter(event: ClickEvent): void {
    switch (event.value.type) {
      case MENU_NOTIFICACAO.MARK_ALL_AS_READ:
        this.confirmarLeituraTodasNotificacoes();
        break;
      case MENU_NOTIFICACAO.MARCAR_COMO_LIDA:
        this.confirmarLeituraNotificacao(event);
        break;
    }
  }

  private confirmarLeituraNotificacao(event: ClickEvent): void {
    if (event.value) {
      const { id: nrSequencia } = event.value.item;
      void HttpService.post(Endpoints.POST.CONFIRMAR_LEITURA_NOTIFICACAO, { nrSequencia }, {})
        .then(() => {
          this.notificationCenter.setReadStatus(nrSequencia, true);
          const qtdUnread = this.notificationCenter.getUnreadNotificationCount();
          this.notificationCenter.setUnreadNotificationsCount(qtdUnread - 1);
        });
    }
  }

  private confirmarLeituraTodasNotificacoes(): void {
    void HttpService.post(Endpoints.POST.CONFIRMAR_LEITURA_TODAS_NOTIFICACOES)
      .then(() => {
        this.notificationCenter.markAllAsRead();
        this.notificacoesNaoLidas();
      });
  }

  private notificacoesNaoLidas(): void {
    HttpService.get(Endpoints.GET.QTDADE_NOTIFICACOES_NAO_LIDAS)
      .then(resp => {
        const { body } = resp;
        const { qtdadeNaoLidas } = body as Notificacao;
        this.notificationCenter.setUnreadNotificationsCount(qtdadeNaoLidas);
      }).catch(() => catchRenderException(() => this.notificationCenter.setUnreadNotificationsCount(0)));
  }

  @OnMount(Aliases.AVATAR_USER)
  onMountAvatar(event: MountEvent): void {

    const api = event.target as AvatarAPI;
    const user = foundationContext.getUser();
    if (user) {
      let username = user.name;
      if (username.length > 30) {
        username = username.substring(0, 30);
        username = `${username}${'...'}`;
      }
      if (!shouldRenderMobile()) {
        api.setTitle(username);
      }
      api.setImage(user.image);
    }
  }

  async checkAditivos(): Promise<void> {
    const { nrSeqBeneficiario } = this.historyService.getCurrentRouteParams();
    await HttpService.get(Endpoints.GET.ADITIVO_CONTRATUAL, { nrSeqBeneficiario }).then(respAviso => {
      const array = (respAviso.body as [AditivoContratual]);
      if (array.length > 0) {
        const listNrSeqTermo = array;
        const nrSeqTermo = array && array[0] ? array[0].nrSeqTermo : '';
        this.context.set({ 'listNrSeqTermo': listNrSeqTermo });
        this.historyService.push(Routes.ASSIGN_ADITIVO_CONTRATUAL, { nrSeqBeneficiario, nrSeqTermo });
      }
    });
  }

}

export {
  ApplicationController,
};
